/**
 * OrgSecuritySettings Component
 * Per-user 2FA management for organization admins.
 * Admins can enable/disable 2FA and set preferred method (SMS/TOTP) for each user.
 */

import React, { useState, useEffect } from 'react';
import { Shield, Check, AlertCircle, X, Phone, Smartphone, Search } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { config } from '@/src/config';
import { cn } from '@/lib/utils';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface OrgSecuritySettingsProps {
  organizationId: number;
  onSettingsChange?: () => void;
}

interface UserTwoFactorInfo {
  user_id: number;
  name: string;
  email: string;
  phone_number: string | null;
  twofa_enabled: boolean;
  preferred_method: string;
  totp_enabled: boolean;
  phone_verified: boolean;
}

export function OrgSecuritySettings({ organizationId, onSettingsChange }: OrgSecuritySettingsProps) {
  const { t } = useAppTranslation();
  const [users, setUsers] = useState<UserTwoFactorInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  };

  useEffect(() => {
    fetchUsers();
  }, [organizationId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/auth/2fa/admin/users/${organizationId}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to load users');
      }
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async (userId: number, enabled: boolean, currentMethod: string) => {
    try {
      setUpdatingUserId(userId);
      setError(null);
      setSuccess(null);

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/auth/2fa/admin/user/${userId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ is_enabled: enabled, preferred_method: currentMethod })
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update 2FA');
      }
      const data = await response.json();
      setSuccess(data.message);
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user 2FA');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleChangeMethod = async (userId: number, method: string, currentEnabled: boolean) => {
    try {
      setUpdatingUserId(userId);
      setError(null);
      setSuccess(null);

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/auth/2fa/admin/user/${userId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ is_enabled: currentEnabled, preferred_method: method })
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update preferred method');
      }
      setSuccess(`Preferred method updated to ${method === 'totp' ? 'Authenticator App' : 'SMS'}`);
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update preferred method');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        Loading security settings...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-blue-600 flex-shrink-0" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.user_2fa_management', 'User Two-Factor Authentication')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings.manage_2fa_per_user', 'Enable or disable 2FA and set the preferred method for each user')}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-600">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
          <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">2FA Enabled</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Preferred Method</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {search ? 'No users match your search' : 'No users found in this organization'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white text-sm">{user.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {user.phone_number ? (
                        <div className="flex items-center gap-1">
                          <span>{user.phone_number}</span>
                          {user.phone_verified && (
                            <Check className="w-3 h-3 text-green-500" />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={user.twofa_enabled}
                        onCheckedChange={(checked) => handleToggle2FA(user.user_id, checked, user.preferred_method)}
                        disabled={updatingUserId === user.user_id}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleChangeMethod(user.user_id, 'sms', user.twofa_enabled)}
                          disabled={updatingUserId === user.user_id}
                          className={cn(
                            "flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors",
                            user.preferred_method === 'sms'
                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                          )}
                        >
                          <Phone className="w-3 h-3" />
                          SMS
                        </button>
                        <button
                          onClick={() => handleChangeMethod(user.user_id, 'totp', user.twofa_enabled)}
                          disabled={updatingUserId === user.user_id}
                          className={cn(
                            "flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors",
                            user.preferred_method === 'totp'
                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                          )}
                        >
                          <Smartphone className="w-3 h-3" />
                          TOTP
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
          {filteredUsers.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              {search ? 'No users match your search' : 'No users found'}
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user.user_id} className="p-4 space-y-3">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                  {user.phone_number && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {user.phone_number}
                      {user.phone_verified && <Check className="w-3 h-3 text-green-500" />}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">2FA Enabled</span>
                  <Switch
                    checked={user.twofa_enabled}
                    onCheckedChange={(checked) => handleToggle2FA(user.user_id, checked, user.preferred_method)}
                    disabled={updatingUserId === user.user_id}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Method</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleChangeMethod(user.user_id, 'sms', user.twofa_enabled)}
                      disabled={updatingUserId === user.user_id}
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors",
                        user.preferred_method === 'sms'
                          ? "bg-blue-100 text-blue-700 border border-blue-300"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      <Phone className="w-3 h-3" />
                      SMS
                    </button>
                    <button
                      onClick={() => handleChangeMethod(user.user_id, 'totp', user.twofa_enabled)}
                      disabled={updatingUserId === user.user_id}
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors",
                        user.preferred_method === 'totp'
                          ? "bg-blue-100 text-blue-700 border border-blue-300"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      <Smartphone className="w-3 h-3" />
                      TOTP
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
